import api from './api';

// ── Types ────────────────────────────────────────────────────────────────────

export interface IndustrializationBrief {
  target_volumes: string | null;
  in_house_intent: string | null;
  cost_lead_priorities: string | null;
  suspected_problem_parts: string[];
  summary: string;
  source: string;
}

export interface BomPart {
  id: string;
  part_number: string | null;
  name: string;
  description: string;
  parent_id: string | null;
  quantity: string | null;
  make_or_buy: 'make' | 'buy' | 'undecided';
  make_buy_rationale: string | null;
  source: string;
  to_verify: boolean;
  created_at: string;
  last_modified_at: string;
}

export interface RiskEntry {
  id: string;
  part_id: string | null;
  part_name: string;
  risk_type: 'critical' | 'single_source' | 'long_lead' | 'hard_to_source';
  severity: 'high' | 'medium' | 'low';
  note: string | null;
  source: string;
  to_verify: boolean;
}

export interface DossierStepStatuses {
  step1_brief: string;
  step2_bom: string;
  step3_make_or_buy: string;
  step4_risk_register: string;
}

export interface IndustrializationDossier {
  id: string;
  solution_id: string;
  sub_project_id: string;
  project_id: string;
  client_id: string;
  brief: IndustrializationBrief | null;
  bom: BomPart[];
  risk_register: RiskEntry[];
  step_statuses: DossierStepStatuses;
  version: number;
  created_at: string;
  last_modified_at: string;
  last_modified_by: string | null;
}

export interface DossierCostEstimate {
  estimated_input_tokens: number;
  estimated_output_tokens: number;
  estimated_cost_cents: number;
  currency: string;
  model_id: string;
  model_name: string;
}

// ── Service ──────────────────────────────────────────────────────────────────

const BASE = '/industrialization';

export const industrializationService = {
  async getDossier(subProjectId: string, solutionId: string): Promise<IndustrializationDossier> {
    const { data } = await api.get(`${BASE}/${subProjectId}/${solutionId}`);
    return data;
  },

  async estimateCost(subProjectId: string, solutionId: string): Promise<DossierCostEstimate> {
    const { data } = await api.post(`${BASE}/estimate`, { sub_project_id: subProjectId, solution_id: solutionId });
    return data;
  },

  async runStep(subProjectId: string, solutionId: string, step: string, language = 'English'): Promise<IndustrializationDossier> {
    const { data } = await api.post(`${BASE}/run-step`, {
      sub_project_id: subProjectId,
      solution_id: solutionId,
      step,
      language,
    });
    return data;
  },

  async updateBom(subProjectId: string, solutionId: string, bom: BomPart[], version: number): Promise<IndustrializationDossier> {
    const { data } = await api.put(`${BASE}/update-bom`, { sub_project_id: subProjectId, solution_id: solutionId, bom, version });
    return data;
  },

  async updateRiskRegister(subProjectId: string, solutionId: string, risk_register: RiskEntry[], version: number): Promise<IndustrializationDossier> {
    const { data } = await api.put(`${BASE}/update-risk-register`, { sub_project_id: subProjectId, solution_id: solutionId, risk_register, version });
    return data;
  },

  async deleteDossier(solutionId: string): Promise<void> {
    await api.delete(`${BASE}/${solutionId}`);
  },
};
